import { Card, CardContent } from '@/components/ui/card';
import { Zap, Infinity as InfinityIcon } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { cn } from '@/lib/utils';
import type { PortfolioAiCredits } from '@/hooks/useAgencyAggregatedData';

interface Props {
  data: PortfolioAiCredits;
}

/**
 * Aggregated AI Credits widget for the agency Plan view.
 *
 * Sprint dashboard_alignment_v1 (26/05/26 22:00) — replaces the
 * misleading single-company `CapUsageWidget("AI Credits")` that
 * previously lived in `AgencyPlanDashboard` and pulled from
 * `useBillingStatus(selectedCompany)`. The plan view must show
 * portfolio-wide signals, not per-tenant ones.
 *
 * Source: `useAgencyAggregatedData.portfolioAiCredits` — sum of
 * `ai_company.granted` and `.remaining` across every active client.
 */
export function PortfolioAiCreditsWidget({ data }: Props) {
  const { t } = useLanguage();

  if (data.anyUnlimited) {
    return (
      <Card className="border-border/50">
        <CardContent className="pt-4 pb-4 px-5 space-y-2">
          <div className="flex items-center gap-2">
            <div className="h-7 w-7 rounded-md bg-primary/10 flex items-center justify-center">
              <Zap className="h-4 w-4 text-primary" />
            </div>
            <p className="text-sm font-medium">{t('dashboard.portfolio_aiCredits.title')}</p>
          </div>
          <div className="flex items-center gap-2 text-primary">
            <InfinityIcon className="h-5 w-5" />
            <span className="text-base font-semibold">
              {t('dashboard.portfolio_aiCredits.unlimited')}
            </span>
          </div>
          <p className="text-[11px] text-muted-foreground">
            {t('dashboard.portfolio_aiCredits.subtitle')}
          </p>
        </CardContent>
      </Card>
    );
  }

  const { totalGranted, totalUsed, totalRemaining } = data;
  const pct = totalGranted > 0 ? Math.min(100, Math.round((totalUsed / totalGranted) * 100)) : 0;
  const tone =
    pct >= 100 ? 'bg-red-500'
      : pct >= 80 ? 'bg-orange-500'
        : pct >= 51 ? 'bg-yellow-500'
          : 'bg-primary';

  return (
    <Card className="border-border/50">
      <CardContent className="pt-4 pb-4 px-5 space-y-2.5">
        <div className="flex items-center gap-2">
          <div className="h-7 w-7 rounded-md bg-primary/10 flex items-center justify-center">
            <Zap className="h-4 w-4 text-primary" />
          </div>
          <p className="text-sm font-medium">{t('dashboard.portfolio_aiCredits.title')}</p>
        </div>
        <div className="flex items-end justify-between">
          <p className="text-2xl font-semibold tabular-nums leading-none">
            {totalUsed}
            <span className="text-base text-muted-foreground"> / {totalGranted}</span>
          </p>
          <p className="text-xs text-muted-foreground tabular-nums">
            {t('dashboard.portfolio_aiCredits.remaining', { remaining: totalRemaining })}
          </p>
        </div>
        <div className="h-2 w-full bg-secondary rounded-full overflow-hidden">
          <div
            className={cn('h-full transition-all', tone)}
            style={{ width: `${pct}%` }}
          />
        </div>
        <p className="text-[11px] text-muted-foreground">
          {t('dashboard.portfolio_aiCredits.subtitle')}
        </p>
      </CardContent>
    </Card>
  );
}
